import React from "react";
import "./BusinessProfileQuestionsEditor.css";

const QUESTION_TYPES = [
  { value: "select", label: "Single choice" },
  { value: "multi-select", label: "Multiple choice" },
  { value: "text", label: "Short answer" },
  { value: "textarea", label: "Long answer" },
  { value: "date", label: "Date" },
];

const BusinessProfileQuestionsEditor = ({
  sections,
  onChange,
  eyebrow = "CLIENT ONBOARDING",
  title = "Business readiness questions",
  description = "Edit what clients see, organize questions by section, or add and remove questions. Changes appear in the client flow after saving.",
  questionLimit = 9,
}) => {
  const questionCount = sections.reduce(
    (total, section) => total + section.questions.length,
    0,
  );

  const updateSection = (sectionIndex, updates) => {
    onChange(
      sections.map((section, index) =>
        index === sectionIndex ? { ...section, ...updates } : section,
      ),
    );
  };

  const updateQuestion = (sectionIndex, questionIndex, updates) => {
    const section = sections[sectionIndex];
    updateSection(sectionIndex, {
      questions: section.questions.map((question, index) =>
        index === questionIndex ? { ...question, ...updates } : question,
      ),
    });
  };

  const addQuestion = (sectionIndex) => {
    const section = sections[sectionIndex];
    updateSection(sectionIndex, {
      questions: [
        ...section.questions,
        {
          id: `question-${Date.now()}`,
          label: "",
          type: "text",
          options: [],
        },
      ],
    });
  };

  const removeQuestion = (sectionIndex, questionIndex) => {
    const section = sections[sectionIndex];
    updateSection(sectionIndex, {
      questions: section.questions.filter((_, index) => index !== questionIndex),
    });
  };

  return (
    <div className="business-question-editor">
      <div className="business-question-editor-heading">
        <div>
          <span className="business-question-eyebrow">{eyebrow}</span>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <span className="business-question-total">
          {questionCount} / {questionLimit} questions
        </span>
      </div>

      {sections.map((section, sectionIndex) => (
        <section className="business-question-section" key={section.title + sectionIndex}>
          <div className="business-question-section-heading">
            <span className="business-question-section-number">
              {String(sectionIndex + 1).padStart(2, "0")}
            </span>
            <div className="business-question-section-fields">
              <label>
                Section title
                <input
                  value={section.title}
                  onChange={(event) =>
                    updateSection(sectionIndex, { title: event.target.value })
                  }
                  placeholder="Section title"
                />
              </label>
              <label>
                Section description
                <input
                  value={section.description || ""}
                  onChange={(event) =>
                    updateSection(sectionIndex, {
                      description: event.target.value,
                    })
                  }
                  placeholder="Short guidance shown to clients"
                />
              </label>
            </div>
          </div>

          <div className="business-question-list">
            {section.questions.map((question, questionIndex) => (
              <article className="business-question-card" key={question.id}>
                <div className="business-question-card-top">
                  <span>Question {questionIndex + 1}</span>
                  <button
                    type="button"
                    className="business-question-remove"
                    onClick={() => removeQuestion(sectionIndex, questionIndex)}
                    aria-label={`Remove question ${questionIndex + 1}`}
                  >
                    Remove
                  </button>
                </div>
                <label>
                  Question text
                  <textarea
                    value={question.label}
                    onChange={(event) =>
                      updateQuestion(sectionIndex, questionIndex, {
                        label: event.target.value,
                      })
                    }
                    rows={2}
                    placeholder="Write a clear, concise question"
                  />
                </label>
                <div className="business-question-fields">
                  <label>
                    Answer type
                    <select
                      value={question.type}
                      onChange={(event) =>
                        updateQuestion(sectionIndex, questionIndex, {
                          type: event.target.value,
                          options: ["select", "multi-select"].includes(
                            event.target.value,
                          )
                            ? question.options || []
                            : [],
                        })
                      }
                    >
                      {QUESTION_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {["select", "multi-select"].includes(question.type) && (
                    <label>
                      Choices (one per line)
                      <textarea
                        value={(question.options || []).join("\n")}
                        onChange={(event) =>
                          updateQuestion(sectionIndex, questionIndex, {
                            options: event.target.value
                              .split("\n")
                              .map((option) => option.trim())
                              .filter(Boolean),
                          })
                        }
                        rows={3}
                        placeholder={"First choice\nSecond choice"}
                      />
                    </label>
                  )}
                </div>
                {question.type !== "select" &&
                  question.type !== "multi-select" && (
                    <label>
                      Placeholder (optional)
                      <input
                        value={question.placeholder || ""}
                        onChange={(event) =>
                          updateQuestion(sectionIndex, questionIndex, {
                            placeholder: event.target.value,
                          })
                        }
                        placeholder="Hint shown inside the answer field"
                      />
                    </label>
                  )}
              </article>
            ))}
          </div>

          <button
            type="button"
            className="business-question-add"
            onClick={() => addQuestion(sectionIndex)}
            disabled={questionCount >= questionLimit}
          >
            <span aria-hidden="true">+</span> Add a question
          </button>
        </section>
      ))}
    </div>
  );
};

export default BusinessProfileQuestionsEditor;
